import utils from '../utils';
import config from '../config';
import { isAgentSandbox } from '../appRuntimeMode';

const $ = window.$;
let curGISAuthOp = null;
let locallyUnavailable = false;

function hasClientId() {
    const clientId = process.env.VUE_APP_GoogleLoginClientID;
    return typeof clientId === 'string' && clientId.trim() !== '';
}

function getIdentity() {
    return window.google?.accounts?.id;
}

function isAvailable() {
    const identity = getIdentity();
    return !isAgentSandbox() || (hasClientId() && !locallyUnavailable &&
        typeof identity?.initialize === 'function' && typeof identity?.renderButton === 'function');
}

function rejectOperation(operation, error, providerUnavailable = false) {
    if (curGISAuthOp !== operation) return;
    clearTimeout(operation.timeout);
    curGISAuthOp = null;
    if (operation.local && providerUnavailable) locallyUnavailable = true;
    operation.dfd.reject(error);
}

function renderButton(buttonText, buttonId, onClick) {
    if (isAgentSandbox() && !isAvailable()) return false;
    try {
        getIdentity().renderButton(document.getElementById(buttonId), {
            theme: 'outline', type: 'standard', text: buttonText,
            logo_alignment: 'center', width: 340, click_listener: onClick
        });
        return true;
    } catch (error) {
        if (!isAgentSandbox()) throw error;
        locallyUnavailable = true;
        if (curGISAuthOp) rejectOperation(curGISAuthOp, error, true);
        return false;
    }
}

function setupAndInitialiseGIS(isReturningUser, replacePending = false) {
    if (replacePending && curGISAuthOp) rejectOperation(curGISAuthOp, { cancelled: true });
    if (curGISAuthOp) return curGISAuthOp.dfd.promise();
    const operation = { dfd: $.Deferred(), local: isAgentSandbox() };
    const promise = operation.dfd.promise();
    curGISAuthOp = operation;
    const identity = getIdentity();
    if (operation.local && !hasClientId()) {
        rejectOperation(operation, new Error('Google sign-in is unavailable'), true);
        return promise;
    }

    const configurationObject = {
        client_id: process.env.VUE_APP_GoogleLoginClientID,
        callback: response => handleResponse(response, operation),
        use_fedcm_for_prompt: true,
        use_fedcm_for_button: true
    };
    try {
        if (isReturningUser) {
            configurationObject.auto_select = true;
            configurationObject.cancel_on_tap_outside = false;
            if (localStorage.getItem('loginHint')) configurationObject.login_hint = localStorage.getItem('loginHint');
            // A rendered button may wait for a user gesture; only silent startup is timed.
            operation.timeout = setTimeout(() => {
                rejectOperation(operation, new Error('Google sign-in timed out'));
            }, 20000);
        }
        identity.initialize(configurationObject);
        if (curGISAuthOp === operation) locallyUnavailable = false;
    } catch (error) {
        rejectOperation(operation, error, true);
    }
    if (isReturningUser && curGISAuthOp === operation) {
        try {
            identity.prompt();
        } catch (error) {
            // Silent prompt failure does not prevent explicit button sign-in.
            rejectOperation(operation, error);
        }
    }
    return promise;
}

function handleResponse(response, operation) {
    if (curGISAuthOp !== operation) return;
    try {
        const decodedCredentials = utils.parseJwt(response.credential);
        localStorage.setItem('loginHint', decodedCredentials.sub);
        const givenName = typeof decodedCredentials.given_name === 'string' ? decodedCredentials.given_name : config.getTranslationText('2381');
        const familyName = typeof decodedCredentials.family_name === 'string' ? decodedCredentials.family_name : config.getTranslationText('2382');
        clearTimeout(operation.timeout);
        curGISAuthOp = null;
        locallyUnavailable = false;
        operation.dfd.resolve({ credential: response.credential, email: decodedCredentials.email, givenName, familyName });
    } catch (error) {
        rejectOperation(operation, error);
    }
}

function revoke(emailAddress, callback) {
    try {
        const identity = getIdentity();
        if (identity) identity.revoke(emailAddress, callback);
        else callback();
    } catch (error) {
        callback();
    }
}

export { renderButton, isAvailable, setupAndInitialiseGIS, revoke };
